import { userDisplayName, primaryRoleLabel } from '@core/utils/user-identity.utils';
import { Component, inject, ChangeDetectionStrategy, computed, ElementRef, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { RouterModule, Router } from '@angular/router';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MenuService, MenuGroup, MenuItem } from '@core/services/menu.service';
import { HasFeatureDirective } from '@shared/directives/has-feature.directive';
import { AuthService } from '@core/services/auth.service';
import { CommandPaletteService, PaletteItem } from '@core/services/command-palette.service';
import { HotkeyService } from '@core/services/hotkey.service';
import { ModalOpenDirective } from '@shared/directives/modal-open.directive';
// RF-QA-173 · NAMED import, not the default one. `import packageJson from
// '.../package.json'` inlined the whole manifest (11.9 KiB minified —
// dependency maps, every npm script) into the shell's initial chunk to render
// one version string. esbuild tree-shakes a named JSON import down to the
// single exported member, so only the version literal survives. Keep this a
// named import; reverting to a default import re-inlines the manifest.
import { version as appVersion } from '../../../../../package.json';

// UI Components
import { TooltipDirective } from '@shared/components/ui/tooltip/tooltip.directive';
import { DividerComponent } from '@shared/components/ui/divider/divider.component';
import { BadgeComponent, BadgeVariant } from '@shared/components/ui/badge/badge.component';
import { AvatarComponent, AvatarStatus } from '@shared/components/ui/avatar/avatar.component';
import { IconComponent } from '@shared/components/ui/icon';

@Component({
  selector: 'app-sidebar-modern',
  standalone: true,
  imports: [
    RouterModule,
    NgTemplateOutlet,
    AngularSvgIconModule,
    HasFeatureDirective,
    TooltipDirective,
    DividerComponent,
    BadgeComponent,
    AvatarComponent,
    IconComponent,
    ModalOpenDirective
  ],
  templateUrl: './sidebar-modern.component.html',
  styleUrls: ['./sidebar-modern.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarModernComponent {
  menuService = inject(MenuService);
  authService = inject(AuthService);
  router = inject(Router);
  /** GAP-FU-01 · Source for the Recent rail. */
  protected readonly palette = inject(CommandPaletteService);
  protected readonly hotkeys = inject(HotkeyService);
  private readonly mobileDialog = viewChild<ElementRef<HTMLDialogElement>>('mobileDialog');

  /** Rendered in the expanded sidebar footer as `v{{ appVersion }}`. */
  readonly appVersion: string = appVersion;
  expandedItems = new Set<string>();

  /**
   * GAP-FU-01 · Last 5 recent entries, RESOLVED against the live palette pool
   * (resolvedRecent) so every row is directly invocable — stale entries
   * (renamed routes, gated-off features, actions this user can't see) drop
   * out instead of rendering as dead rows.
   */
  protected readonly recentRows = computed(() => this.palette.resolvedRecent().slice(0, 5));

  /** Platform-aware modifier label for the "Quick jump" footer hint. */
  protected readonly modKey = this.hotkeys.modifierLabel();

  protected openPalette(mobile = false): void {
    if (mobile) {
      this.closeMobileMenu();
    }
    this.palette.open();
  }

  protected closeMobileMenu(): void {
    // Restore the drawer opener before another modal (the palette) captures focus.
    this.mobileDialog()?.nativeElement.close();
    this.menuService.closeMobileMenu();
  }

  /**
   * Navigate/run a Recent row directly. Previously rows called openPalette(),
   * which dumped the operator into the ⌘K search instead of opening the item —
   * an extra, surprising hop. invoke() runs the item and re-timestamps it.
   */
  protected invokeRecent(item: PaletteItem, mobile = false): void {
    if (mobile) {
      this.closeMobileMenu();
    }
    this.palette.invoke(item);
  }

  // User profile computed signals
  readonly currentUserName = computed(() => {
    const user = this.authService.currentUser();
    return user ? userDisplayName(user) : 'Guest';
  });
  readonly currentUserRole = computed(() => primaryRoleLabel(this.authService.currentUser()?.roles) ?? 'User');
  readonly currentUserInitial = computed(() => this.currentUserName().charAt(0).toUpperCase());

  readonly connectionStatus = computed((): AvatarStatus => this.authService.isAuthenticated() ? 'online' : 'offline');

  get menu(): MenuGroup[] {
    return this.menuService.visibleMenu();
  }

  get menuItems(): MenuItem[] {
    return this.menu.flatMap(group => group.items || []);
  }

  get isCollapsed(): boolean {
    return !this.menuService.showSideBar();
  }

  toggleSidebar(): void {
    this.menuService.toggleSidebar();
  }

  toggleSubmenu(itemRoute: string): void {
    if (this.expandedItems.has(itemRoute)) {
      this.expandedItems.delete(itemRoute);
    } else {
      this.expandedItems.add(itemRoute);
    }
  }

  isItemExpanded(itemRoute: string): boolean {
    return this.expandedItems.has(itemRoute);
  }

  isActive(route: string): boolean {
    return this.router.isActive(route, {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored'
    });
  }

  getIconPath(icon?: string): string {
    // Convert icon reference to actual SVG path if needed
    return icon || '';
  }

  /**
   * TrackBy functions for *ngFor loops
   * These functions optimize change detection by providing stable identity tracking for list items
   */

  /**
   * TrackBy for menu items - uses route or label as identifier
   */
  trackByMenuItem(_index: number, item: MenuItem): string {
    return item.route || item.label;
  }

  /**
   * TrackBy for child menu items - uses route as identifier
   */
  trackByChildItem(_index: number, child: MenuItem): string {
    return child.route || child.label;
  }

  /**
   * Convert badge color string to BadgeVariant
   */
  getBadgeVariant(color?: string): BadgeVariant {
    const variantMap: Record<string, BadgeVariant> = {
      'primary': 'primary',
      'success': 'success',
      'warning': 'warning',
      'danger': 'danger',
      'info': 'info'
    };
    return variantMap[color || 'default'] || 'default';
  }
}
